/** GPU Lens Correction — fragment-shader port of
 *  `lib/filters/lens-correction.ts`.
 *
 *  Radial barrel/pincushion distortion: for each output pixel at
 *  normalized radius r, sample the source at r' = r * (1 + k * r²).
 *  Positive k pulls edge pixels outward (barrel); negative k pulls
 *  them inward (pincushion).
 *
 *  Bilinear sampling via the runtime's `filter: 'linear'` option —
 *  hardware does the 2×2 weighted average the JS `bilinearSample`
 *  helper computes manually. */

import { applyIdentityCopy } from '../identity-copy';
import type { LensCorrectionValue } from '../../filters/lens-correction';
import { applyShader } from '../runtime';

const LENS_CORRECTION_FRAG = `#version 300 es
precision highp float;
in vec2 v_uv;
uniform sampler2D u_source;
uniform vec2 u_dims;
uniform vec2 u_center;
uniform float u_halfDiag;
uniform float u_k;
out vec4 outColor;

void main() {
  vec2 px = v_uv * u_dims - 0.5;
  vec2 d = (px - u_center) / u_halfDiag;
  float r = length(d);
  // Match the JS r==0 guard. The else-branch's rDistorted/r is the
  // same 1+k*r² scale, computed without the divide for r==0.
  float scale = r < 1e-6 ? 1.0 : (1.0 + u_k * r * r);
  vec2 srcPx = u_center + d * scale * u_halfDiag;
  vec2 srcUv = (srcPx + 0.5) / u_dims;
  outColor = texture(u_source, srcUv);
}
`;

export function applyLensCorrectionOnGpu(
  source: ImageData,
  target: HTMLCanvasElement,
  value: LensCorrectionValue,
): boolean {
  const amount = Math.max(-100, Math.min(100, value.amount));
  if (amount === 0) return applyIdentityCopy(source, target);
  const k = amount / 200; // matches JS scaling
  const w = source.width;
  const h = source.height;
  const cx = (w - 1) / 2;
  const cy = (h - 1) / 2;
  const halfDiag = Math.sqrt(cx * cx + cy * cy);
  return applyShader({
    source,
    target,
    fragShaderSrc: LENS_CORRECTION_FRAG,
    filter: 'linear',
    uniforms: {
      u_dims: { type: 'vec2', value: [w, h] },
      u_center: { type: 'vec2', value: [cx, cy] },
      u_halfDiag: { type: 'float', value: halfDiag },
      u_k: { type: 'float', value: k },
    },
  });
}
